import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { toDataURL } from 'qrcode';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { sessionApi, sessionQuery } from '../api/session';
import { Centered } from '../components/Layout';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import { Alert, Button, Card, DevCode, Loading, RecoveryCodes, TextField } from '@avrahami/ui';

/** Enrollment of the second factor. Platform staff land here until it is done. */
export function MfaSetupPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const session = useQuery(sessionQuery);
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string>();
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>();

  const setup = useMutation({ mutationFn: sessionApi.setupMfa });
  const enable = useMutation({
    mutationFn: sessionApi.enableMfa,
    onSuccess: (result) => {
      setRecoveryCodes(result.recoveryCodes);
    },
  });

  const uri = setup.data?.otpauthUri;
  useEffect(() => {
    if (uri === undefined) return;
    let cancelled = false;
    void toDataURL(uri, { margin: 1, width: 200 }).then((url) => {
      if (!cancelled) setQr(url);
    });
    return () => {
      cancelled = true;
    };
  }, [uri]);

  if (session.isPending) {
    return (
      <Centered>
        <Loading />
      </Centered>
    );
  }
  if (session.data?.kind !== 'signed-in') return <Navigate to="/login" />;
  // Already enrolled (and not in the middle of reading the recovery codes).
  if (session.data.me.mfa.enabled && recoveryCodes === undefined) return <Navigate to="/tenants" />;

  if (recoveryCodes !== undefined) {
    return (
      <Centered>
        <Card title={he.mfaSetup.recoveryTitle}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">{he.mfaSetup.recoveryIntro}</p>
            <RecoveryCodes codes={recoveryCodes} label={he.mfaSetup.recoveryTitle} />
            <Button
              onClick={() => {
                void queryClient.invalidateQueries().then(() => navigate({ to: '/tenants' }));
              }}
            >
              {he.mfaSetup.recoveryDone}
            </Button>
          </div>
        </Card>
      </Centered>
    );
  }

  const onEnable = (event: SyntheticEvent) => {
    event.preventDefault();
    enable.mutate(code.trim());
  };

  return (
    <Centered>
      <Card title={he.mfaSetup.title}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">{he.mfaSetup.intro}</p>
          {setup.data === undefined ? (
            <Button
              busy={setup.isPending}
              onClick={() => {
                setup.mutate();
              }}
            >
              {he.mfaSetup.start}
            </Button>
          ) : (
            <form onSubmit={onEnable} className="flex flex-col gap-4">
              {qr === undefined ? null : (
                <img
                  src={qr}
                  alt={he.mfaSetup.qrAlt}
                  width={200}
                  height={200}
                  className="self-center"
                />
              )}
              <div className="text-sm text-slate-600">
                {he.mfaSetup.manual}
                <div
                  className="ltr mt-1 rounded bg-slate-100 p-2 font-mono text-xs break-all select-all"
                  data-testid="mfa-secret"
                >
                  {setup.data.secret}
                </div>
              </div>
              <DevCode
                code={setup.data.devCode}
                onUse={setCode}
                label={he.devCode.label}
                useLabel={he.devCode.use}
              />
              <TextField
                label={he.mfaSetup.code}
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
                className="ltr"
              />
              {enable.isError ? <Alert>{errorMessage(enable.error)}</Alert> : null}
              <Button type="submit" busy={enable.isPending}>
                {he.mfaSetup.enable}
              </Button>
            </form>
          )}
          {setup.isError ? <Alert>{errorMessage(setup.error)}</Alert> : null}
        </div>
      </Card>
    </Centered>
  );
}
